'use client';

/**
 * PremiumHero — fullscreen cinematic hero per la nuova homepage Insula Charter.
 * Riusa sistema upload admin (settings.hero-background) per slideshow immagini.
 * Fallback: immagini destinations + gradient cinematici.
 */

import { useEffect, useState } from 'react';
import { ArrowRight, Anchor, Compass, ChevronDown } from 'lucide-react';
import { useContentImage } from '@/components/visual-editing/PageContentProvider';

interface PremiumHeroProps {
  /**
   * Quando true, renderizza solo lo slideshow di background (no headline, CTA,
   * scroll cue). Usato per posizionare l'hero come BG fisso dietro lo snap-root,
   * con content overlay gestito dal parent. Default false.
   */
  decorative?: boolean;
  /** Immagini pre-fetched server-side (skip fetch client). */
  initialImages?: string[];
  /** Rotate interval pre-fetched server-side. */
  initialRotateMs?: number;
}

export default function PremiumHero({ decorative = false, initialImages, initialRotateMs }: PremiumHeroProps = {}) {
  // Initial state da SSR props quando disponibili → primo paint con hero già
  // visibile (no flash slate + ritardo fetch).
  const [images, setImages] = useState<string[]>(initialImages || []);
  const [rotateMs, setRotateMs] = useState(initialRotateMs || 7000);
  const [currentIdx, setCurrentIdx] = useState(0);
  // Override contenuto editabile: se l'owner imposta hero.bgImage, sostituisce
  // lo slideshow con quella singola immagine. Vuoto = slideshow default.
  const bgOverride = useContentImage('hero.bgImage', '');
  const effectiveImages = bgOverride ? [bgOverride] : images;

  // Skip fetch client se initial già passato → 1 round-trip in meno.
  useEffect(() => {
    if (initialImages && initialImages.length > 0) return;
    fetch('/api/settings/public/hero-background')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.success && Array.isArray(data.data?.urls) && data.data.urls.length > 0) {
          setImages(data.data.urls);
          if (typeof data.data?.rotateMs === 'number' && data.data.rotateMs > 0) setRotateMs(data.data.rotateMs);
        }
      })
      .catch(() => {});
  }, [initialImages]);

  // Slideshow auto-rotazione
  useEffect(() => {
    if (effectiveImages.length <= 1) return;
    const t = setInterval(() => setCurrentIdx(i => (i + 1) % effectiveImages.length), rotateMs);
    return () => clearInterval(t);
  }, [effectiveImages.length, rotateMs]);

  return (
    <section
      data-cid="premium-hero"
      className="relative w-full h-full min-h-[100svh] flex items-center overflow-hidden bg-slate-900 pt-20"
    >
      {/* Background slideshow via <img> diretto su WebP STATICI pre-generati
          (sibling .webp, q85, dimensioni native — vedi scripts/gen-hero-webp).
          Niente /_next/image: zero generazione on-demand, zero cache da
          riscaldare, nessuna acrobazia di preload. Il browser scarica il file
          statico (~260-380KB) e lo cachea via HTTP. Sorgenti remote (http) →
          servite as-is. Prima slide eager+high priority. */}
      <div className="absolute inset-0 z-0" data-cid="premium-hero-bg-image">
        {effectiveImages.map((url, i) => (
          <div
            key={url + i}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${
              i === currentIdx ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ willChange: 'opacity' }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={url.startsWith('http') ? url : url.replace(/\.(png|jpe?g)$/i, '.webp')}
              alt=""
              fetchPriority={i === 0 ? 'high' : undefined}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        ))}
        {/* Placeholder/fallback: tono scuro coerente con l'hero overlay-ato
            (vedi overlay slate-950/40-70 sotto) → il caricamento dell'immagine
            non produce flash blu acceso, solo un dark cinematico già nel tema. */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 -z-10" />
      </div>

      {/* Overlay scuro elegante per leggibilità + atmosfera cinematica */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-slate-950/60 via-slate-900/40 to-slate-950/70" />

      {/* Content — nascosto in modalità decorative (BG-only) */}
      {!decorative && (
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-[11px] font-bold uppercase tracking-[0.18em] mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            Sardegna · Mediterraneo
          </span>

          <h1 className="font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6">
            Che esperienza vuoi vivere<br />
            in barca <em className="font-serif italic font-medium text-cyan-200/95">oggi</em> in Sardegna?
          </h1>

          <p className="text-white/85 text-base md:text-lg lg:text-xl font-light leading-relaxed max-w-2xl mb-10">
            Noleggi, escursioni ed esperienze autentiche per vivere il mare della Sardegna.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-cid="premium-hero-cta-experiences"
              onClick={() => {
                const el = document.getElementById('esperienze');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-slate-900 font-bold text-sm uppercase tracking-wider hover:bg-cyan-50 transition-all shadow-2xl shadow-black/30 active:scale-[0.98]"
            >
              <Compass size={16} className="text-emerald-600" />
              Esplora esperienze
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              data-cid="premium-hero-cta-rentals"
              onClick={() => {
                const el = document.getElementById('ricerca');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/30 font-bold text-sm uppercase tracking-wider hover:bg-white/20 transition-all active:scale-[0.98]"
            >
              <Anchor size={16} />
              Prenota la tua barca
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Scroll cue — 3 chevron lampeggianti, click → scroll a #esperienze.
          Nascosto in decorative (gestito dal parent overlay). */}
      {!decorative && (
      <button
        type="button"
        data-cid="premium-hero-scroll-cue"
        aria-label="Scorri alla prossima sezione"
        onClick={() => {
          const el = document.getElementById('esperienze');
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        className="group absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl hover:bg-white/5 transition-colors active:scale-95"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 group-hover:text-white/90 transition-colors">
          Scorri
        </span>
        <ChevronDown
          size={22}
          strokeWidth={2}
          className="text-white/70 group-hover:text-white animate-pulse transition-colors"
          style={{ animationDuration: '2.5s', animationDelay: '0ms' }}
        />
        <ChevronDown
          size={22}
          strokeWidth={2}
          className="-mt-3 text-white/70 group-hover:text-white animate-pulse transition-colors"
          style={{ animationDuration: '2.5s', animationDelay: '300ms' }}
        />
        <ChevronDown
          size={22}
          strokeWidth={2}
          className="-mt-3 text-white/70 group-hover:text-white animate-pulse transition-colors"
          style={{ animationDuration: '2.5s', animationDelay: '600ms' }}
        />
      </button>
      )}
    </section>
  );
}
